import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ordersAPI } from '../../services/api';
import WalletWidget from '../wallet/WalletWidget';
import { WelcomePopup, RechargeReminder, SetupChecklist } from './OnboardingGates';
import ReleaseNotesModal from '../common/ReleaseNotesModal';
import MaintenanceBanner from '../common/MaintenanceBanner';
import StatusWidget from '../status/StatusWidget';
import StatusBanner from '../status/StatusBanner';
import NavSearch from './NavSearch';
import { BulkProgressProvider } from '../../contexts/BulkProgressContext';
import BulkProgressCards from '../common/BulkProgressCards';
import {
  LayoutDashboard, Package, Store, MapPin,
  Archive, Building2, ClipboardList, CreditCard,
  Pin, PinOff, ChevronDown, Search, Menu, Banknote, MoreVertical, Settings, LogOut, Clock, Truck, Send, Wallet, Receipt, FileCheck2, Puzzle, Users, Undo2, RotateCcw, AlertTriangle, ListChecks, FileDown, Sparkles,
} from 'lucide-react';

// A12: `mod` is the access module that owns each entry. A menu item the user has no access to is
// HIDDEN rather than greyed out — a sidebar full of things that 403 on click is just confusing, and
// the confusion is the whole reason this exists. Section headers disappear with their contents.
//
// Note `Settings` is deliberately gated on `setup` even though the settings API serves GETs to
// everyone: those reads exist so staff can PRINT (labels need the account's template), not so the
// Settings screen is browsable. Menu visibility is a product decision, not a mirror of the API gate.
// v1.239.0 - grouped menu: ~10 rows instead of 22, ordered the way the day flows (orders → ship →
// returns → money → stock). A `group` row folds open to show its `children`; nothing was removed,
// every page is at most one click deeper. Billing is reached from the Wallet card (HIDDEN_NAV keeps it
// known to canAccessPath and to Search pages).
const NAV = [
  // A82 - ModIQ sits ABOVE Dashboard and is marked `premium`, which is the only thing that styles
  // a row differently in this otherwise deliberately uniform sidebar. It is shown to everyone,
  // subscriber or not: the page itself is gated server-side and renders an upsell when it is off,
  // so hiding the row would sell nothing and a row that vanishes on downgrade would just confuse.
  // v1.247.0 - ModIQ is a premium GROUP: the live board + Profit & Loss.
  { group: 'modiq', label: 'ModIQ', Icon: Sparkles, premium: true, children: [
    { path: '/dashboard/modiq',            label: 'Live board',             Icon: Sparkles, mod: 'orders', premium: true },
    { path: '/dashboard/modiq/profit',     label: 'Profit & Loss',          Icon: Sparkles, mod: 'finance', premium: true },
  ] },
  { path: '/dashboard',                  label: 'Dashboard',              Icon: LayoutDashboard, mod: 'orders' },
  { search: true },
  { section: 'Daily work' },
  { path: '/dashboard/orders',           label: 'Orders',                 Icon: Package, badge: 'attention', mod: 'orders' },
  // A64 — NDR panel hidden 2026-09-11 pending further design. To restore, put this line back AND
  // set NDR_PANEL_ENABLED=true in the server .env (the routes 404 without it):
  //   { path: '/dashboard/ndr', label: 'NDR', Icon: AlertTriangle, mod: 'orders' },
  { group: 'shipping', label: 'Shipping', Icon: Truck, children: [
    { path: '/dashboard/pickups',          label: 'Pickups',                Icon: Truck, mod: 'orders' },
    { path: '/dashboard/bulk-actions',     label: 'Bulk Actions',           Icon: ListChecks, mod: 'orders' },
  ] },
  { group: 'returns', label: 'Returns', Icon: Undo2, children: [
    { path: '/dashboard/return-exchange',  label: 'Return / Exchange',      Icon: Undo2, mod: 'orders' },
    { path: '/dashboard/rto-reconcile',    label: 'RTO / Return Reconcile', Icon: RotateCcw, mod: 'reconciliation' },
  ] },
  { path: '/dashboard/customers',        label: 'Customers',              Icon: Users, mod: 'customers' },
  { section: 'Money' },
  { group: 'payments', label: 'Payments', Icon: Banknote, children: [
    { path: '/dashboard/payments/remittance',     label: 'Remittance',          Icon: Wallet, mod: 'finance' },
    { path: '/dashboard/payments/shipping-cost',  label: 'Shipping Cost',       Icon: Receipt, mod: 'finance' },
    { path: '/dashboard/payments/cost-reconcile', label: 'Cost Reconciliation', Icon: FileCheck2, mod: 'finance' },
    { path: '/dashboard/payments/vendor',         label: 'Vendor Payments',     Icon: CreditCard, mod: 'finance' },
    { path: '/dashboard/payments/credit-note',    label: 'Credit Note',         Icon: Undo2, mod: 'finance' },
  ] },
  { section: 'Stock' },
  { group: 'inventory', label: 'Inventory', Icon: Archive, children: [
    { path: '/dashboard/products',         label: 'Products',               Icon: Archive, mod: 'purchase' },
    { path: '/dashboard/vendors',          label: 'Vendors',                Icon: Building2, mod: 'purchase' },
    { path: '/dashboard/purchase-manager', label: 'Purchase Manager',       Icon: ClipboardList, mod: 'purchase' },
  ] },
  { path: '/dashboard/reports',          label: 'Reports',                Icon: FileDown, mod: 'orders' },
];
// Set-once pages, pinned to the bottom of the panel above the wallet.
const BOTTOM_NAV = [
  { group: 'setup', label: 'Setup', Icon: Settings, children: [
    { path: '/dashboard/stores',           label: 'Sales Channels',         Icon: Store, mod: 'setup' },
    { path: '/dashboard/couriers',         label: 'Couriers',               Icon: Send, mod: 'setup' },
    { path: '/dashboard/locations',        label: 'Locations',              Icon: MapPin, mod: 'setup' },
    { path: '/dashboard/mod-addons',       label: 'Mod Add-ons',            Icon: Puzzle, mod: 'setup' },
    { path: '/dashboard/settings',         label: 'Settings',               Icon: Settings, mod: 'setup' },
  ] },
];
// Not a menu row, but still a page with an access rule (opened from the Wallet card).
const HIDDEN_NAV = [
  { path: '/dashboard/billing',          label: 'Billing',                Icon: Wallet, mod: 'billing' },
];
// Every page, flat, with the group it lives under (for canAccessPath and Search pages).
const ALL_PAGES = [...NAV, ...BOTTOM_NAV, ...HIDDEN_NAV].flatMap(n =>
  n.group ? n.children.map(c => ({ ...c, group: n.label })) : (n.path ? [n] : []));
const canOpen = (n, access) => !access || !n.mod || (access[n.mod] && access[n.mod] !== 'none');

const DEFAULT_TITLE = 'ModCommerce — Multichannel Order Management';
// Pages with a route but no menu row.
const EXTRA_TITLES = { '/dashboard/status': 'System Status', '/dashboard/profile': 'Profile', '/dashboard/branded-tracking': 'Branded Tracking' };
const pageTitle = (pathname) => {
  // Longest match wins, so /dashboard/settings/team is Settings. /dashboard itself only matches exactly.
  const page = ALL_PAGES
    .filter(n => n.path === pathname || (n.path !== '/dashboard' && pathname.startsWith(`${n.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0];
  const label = page?.label || EXTRA_TITLES[pathname];
  return label ? `${label} · ModCommerce` : DEFAULT_TITLE;
};

/**
 * Can this user open this URL? Used by ProtectedRoute so a bookmark or typed URL cannot land on a
 * page whose every request 403s. Matches the LONGEST nav path that prefixes the URL, so
 * `/dashboard/settings/team` is judged by `/dashboard/settings`.
 *
 * Fails OPEN for anything not in the menu (e.g. `/dashboard/profile`): the API is the real gate, and
 * wrongly locking someone out of their own profile page is a worse failure than showing a page that
 * turns out to be empty.
 */
export const canAccessPath = (pathname, access) => {
  if (!access) return true; // owner / pre-A12
  let best = null;
  for (const n of ALL_PAGES) {
    if (pathname === n.path || pathname.startsWith(n.path + '/')) {
      if (!best || n.path.length > best.path.length) best = n;
    }
  }
  if (!best || !best.mod) return true;
  // Only '/dashboard' matched but the URL is deeper ⇒ an unlisted sub-route ⇒ allow.
  if (best.path === '/dashboard' && pathname !== '/dashboard') return true;
  return !!access[best.mod] && access[best.mod] !== 'none';
};

// Keep only what this user can reach, then drop any section header left with nothing under it.
// `access` is computed server-side (auth middleware) from the same table the API gates use, so the
// menu can never disagree with what the server will actually allow. Absent ⇒ owner ⇒ everything.
const visibleNav = (list, access) => {
  const kept = list
    .map(n => n.group ? { ...n, children: n.children.filter(c => canOpen(c, access)) } : n)
    .filter(n => n.section || n.search || (n.group ? n.children.length > 0 : canOpen(n, access)));
  return kept.filter((n, i) => {
    if (!n.section) return true;
    const next = kept[i + 1];
    return next && !next.section; // header with no items after it
  });
};
const onPath = (pathname, p) => pathname === p || pathname.startsWith(p + '/');

function PendingOverlay({ name, onLogout }) {
  return (
    <div style={ov.overlay}>
      <div style={ov.card}>
        <div style={ov.iconWrap}><Clock size={28} /></div>
        <h2 style={ov.title}>Account Pending Approval</h2>
        <p style={ov.msg}>
          Hi <strong style={{ color: '#0f172a' }}>{name}</strong>, your account is awaiting
          approval from our team. You'll gain full access once approved.
        </p>
        <div style={ov.note}>We typically review accounts within 24 hours. For more info, contact us at <a href="mailto:support@modcommerce.in" style={{ color: '#2563eb', fontWeight: 600 }}>support@modcommerce.in</a>.</div>
        <button style={ov.logoutBtn} onClick={onLogout}>Logout</button>
      </div>
    </div>
  );
}

const ov = {
  overlay: { position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', background: 'rgba(15,23,42,0.55)' },
  card: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '40px 48px', maxWidth: 460, width: '90%', textAlign: 'center', boxShadow: 'var(--mc-e4)' },
  iconWrap: { width: 60, height: 60, margin: '0 auto 16px', borderRadius: 16, background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { color: '#0f172a', fontSize: 22, fontWeight: 700, margin: '0 0 12px' },
  msg: { color: '#64748b', fontSize: 15, lineHeight: 1.6, margin: '0 0 16px' },
  note: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 16px', color: '#94a3b8', fontSize: 13, marginBottom: 24 },
  logoutBtn: { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};

// Wrapped so bulk-action progress lives ABOVE the routed page. A batch started on Orders keeps
// reporting while the operator works anywhere else — which is the whole point of the feature.
export default function Layout(props) {
  return (
    <BulkProgressProvider>
      <LayoutInner {...props} />
    </BulkProgressProvider>
  );
}

// True below 768px. Every page is styled inline (no media queries possible), so the shell is the one
// place that adapts: drawer navigation here, horizontal table scroll in index.css (.mc-main).
const NARROW_Q = '(max-width: 768px)';
function useNarrow() {
  const [narrow, setNarrow] = useState(() => {
    try { return window.matchMedia(NARROW_Q).matches; } catch (_) { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(NARROW_Q); } catch (_) { return undefined; }
    const on = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return narrow;
}

function LayoutInner({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // v1.238.0 - two modes. PINNED = always full width, the page makes room. AUTO-HIDE = a 64px icon
  // rail that slides open over the page on hover and closes when the mouse leaves. The choice is
  // remembered per browser. Devices with no hover (touch) default to pinned, since a hover-only menu
  // would be unreachable there.
  const [pinned, setPinned] = useState(() => {
    try {
      const v = localStorage.getItem('oms_nav_pinned');
      if (v != null) return v === '1';
      return !!(window.matchMedia && window.matchMedia('(hover: none)').matches);
    } catch (_) { return false; }
  });
  const [hoverOpen, setHoverOpen] = useState(false);
  // Groups the user left open (remembered). The group holding the current page opens regardless.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('oms_nav_groups') || '{}') || {}; } catch (_) { return {}; }
  });
  const toggleGroup = (g) => setOpenGroups(o => {
    const nx = { ...o, [g]: !o[g] };
    try { localStorage.setItem('oms_nav_groups', JSON.stringify(nx)); } catch (_) {}
    return nx;
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const hoverTimer = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Phones and narrow windows: the sidebar becomes an off-canvas drawer behind a menu button, so the
  // page gets the full width. Pin and hover do not apply there (there is no hover on touch).
  const narrow = useNarrow();
  const [drawer, setDrawer] = useState(false);
  const expanded = narrow ? drawer : (pinned || hoverOpen || menuOpen);
  const searchPages = ALL_PAGES.filter(n => canOpen(n, user?.access));
  const collapsed = !expanded;
  const location = useLocation();
  const togglePin = () => setPinned(p => {
    const nx = !p;
    try { localStorage.setItem('oms_nav_pinned', nx ? '1' : '0'); } catch (_) {}
    if (!nx) setHoverOpen(false);
    return nx;
  });
  // Small open delay so a mouse crossing the rail on its way somewhere doesn't fling it open; a
  // longer close delay so a brief slip off the edge doesn't snap it shut.
  const onRailEnter = () => { clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(() => setHoverOpen(true), 90); };
  const onRailLeave = () => { clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(() => setHoverOpen(false), 300); };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  // Picking a page in auto-hide mode closes the panel straight away — the user is done with it.
  useEffect(() => { if (!pinned) { clearTimeout(hoverTimer.current); setHoverOpen(false); } }, [location.pathname]); // eslint-disable-line
  useEffect(() => { setDrawer(false); setMenuOpen(false); }, [location.pathname, narrow]);
  useEffect(() => {
    document.title = pageTitle(location.pathname);
    return () => { document.title = DEFAULT_TITLE; };
  }, [location.pathname]);
  // `[` toggles the pin (ignored while typing).
  useEffect(() => {
    const onKey = (e) => {
      // Ctrl/⌘ + K - Search pages, from anywhere (even while typing: it is the universal shortcut).
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setSearchOpen(o => !o); return; }
      if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      togglePin();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line
  const [attnCount, setAttnCount] = useState(0); // Need Attention sidebar badge
  const menuRef = useRef(null);

  const handleLogout = () => { logout(); navigate('/user'); };

  // Poll the pending (Need Attention) count for the sidebar badge.
  // Layout wraps every authenticated page, so this tick runs for the whole session. Skip it while
  // the tab is in the background — a forgotten tab was polling all day and nobody was looking at
  // the badge. Refresh once on the way back so the number is current when the user returns.
  // `bg` marks the automatic heartbeat: the server may answer it from a briefly-stale cache so a
  // bulk ship doesn't force a full re-scan on every mutation. First paint and the refresh when the
  // user comes back to the tab are NOT background — those ask for exact numbers.
  useEffect(() => {
    let alive = true;
    const load = (bg) => ordersAPI.getCounts(bg ? { bg: 1 } : undefined)
      .then(r => { if (alive) { const c = r.data.counts || {}; setAttnCount((c.pending || 0) + (c.prepaid_rto || 0)); } })
      .catch(() => {});
    const tick = () => { if (document.visibilityState === 'visible') load(true); };
    const onVisible = () => { if (document.visibilityState === 'visible') load(false); };
    load(false);
    const id = setInterval(tick, 60000);
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isPending = user?.approval_status === 'pending';

  // One leaf row. `sub` = inside a group (indented, no icon, slimmer).
  const renderLink = ({ path, label, Icon, badge, premium }, sub) => (
    <NavLink
      key={path}
      to={path}
      // A63: a stable handle for the finished bulk card to fly to. Keyed off the ROUTE, not
      // the label, so translating or renaming the item cannot silently break the animation.
      data-nav={sub === 'hidden' ? undefined : path.replace('/dashboard/', '').replace('/dashboard', 'home')}
      end={path === '/dashboard' || path === '/dashboard/modiq'}
      className="mc-nav"
      tabIndex={sub === 'hidden' ? -1 : undefined}
      style={({ isActive }) => ({
        ...styles.navLink,
        ...(sub ? styles.subLink : null),
        background: premium
          ? (isActive ? 'linear-gradient(135deg,#6366F1,#A855F7)' : 'transparent')
          : (isActive ? 'rgba(255,255,255,0.09)' : 'transparent'),
        color: premium ? (isActive ? '#FFFFFF' : '#C4B5FD') : (isActive ? '#FFFFFF' : '#A1A1A6'),
        fontWeight: isActive || premium ? 600 : 500,
        boxShadow: premium && isActive ? '0 6px 18px rgba(99,102,241,0.35)' : 'none',
      })}
      title={collapsed ? label : undefined}
    >
      {({ isActive }) => (<>
        {!sub && <Icon size={18} strokeWidth={1.9} style={{ flexShrink: 0 }} />}
        <span style={{ ...styles.navLabel, opacity: collapsed ? 0 : 1 }}>{label}</span>
        {/* The PRO pill has to know `isActive` too: the active row's background IS the
            purple gradient, and a purple-on-purple pill would disappear into it. */}
        {premium && !collapsed && (
          <span style={isActive ? styles.navProOn : styles.navPro}>PRO</span>
        )}
        {badge === 'attention' && attnCount > 0 && (
          collapsed
            ? <span style={styles.badgeDot} />
            : <span style={styles.navBadge}>{attnCount > 99 ? '99+' : attnCount}</span>
        )}
      </>)}
    </NavLink>
  );

  const renderNav = (n) => {
    if (n.section) return (
      <div key={n.section} style={styles.navSection}>
        <span style={{ opacity: collapsed ? 0 : 1, transition: 'opacity 150ms ease' }}>{n.section}</span>
        {collapsed && <span style={styles.navDivider} />}
      </div>
    );
    if (n.search) return (
      <button key="search" className="mc-nav" style={{ ...styles.navLink, ...styles.searchRow }} onClick={() => setSearchOpen(true)} title={collapsed ? 'Search pages (Ctrl+K)' : undefined}>
        <Search size={18} strokeWidth={1.9} style={{ flexShrink: 0 }} />
        <span style={{ ...styles.navLabel, opacity: collapsed ? 0 : 1, flex: 1, textAlign: 'left' }}>Search pages</span>
        <span style={{ ...styles.kbd, opacity: collapsed ? 0 : 1 }}>Ctrl K</span>
      </button>
    );
    if (n.group) {
      const here = n.children.some(c => onPath(location.pathname, c.path));
      const isOpen = !collapsed && (openGroups[n.group] || here);
      // When folded, the group row stands in for its children as the bulk-card fly target.
      const flyTo = !isOpen && n.children.some(c => c.path === '/dashboard/bulk-actions') ? 'bulk-actions' : undefined;
      return (
        <div key={n.group}>
          <button
            className="mc-nav" data-nav={flyTo}
            onClick={() => toggleGroup(n.group)}
            aria-expanded={isOpen}
            title={collapsed ? `${n.label}: ${n.children.map(c => c.label).join(', ')}` : undefined}
            style={{
              ...styles.navLink, ...styles.groupRow,
              background: here && collapsed ? 'rgba(255,255,255,0.09)' : 'transparent',
              color: n.premium ? (here ? '#FFFFFF' : '#C4B5FD') : (here ? '#FFFFFF' : '#A1A1A6'), fontWeight: here || n.premium ? 600 : 500,
            }}
          >
            <n.Icon size={18} strokeWidth={1.9} style={{ flexShrink: 0 }} />
            <span style={{ ...styles.navLabel, opacity: collapsed ? 0 : 1, flex: 1, textAlign: 'left' }}>{n.label}</span>
            {n.premium && !collapsed && <span style={styles.navPro}>PRO</span>}
            <ChevronDown size={15} style={{ ...styles.chev, opacity: collapsed ? 0 : 0.7, transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
          </button>
          {/* grid-rows 0fr↔1fr animates to the content's real height without measuring it */}
          <div style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', transition: `grid-template-rows 240ms ${EASE}` }}>
            <div style={{ overflow: 'hidden', position: 'relative' }}>
              <span style={styles.guide} />
              {n.children.map(c => renderLink(c, isOpen ? 'sub' : 'hidden'))}
            </div>
          </div>
        </div>
      );
    }
    return renderLink(n, false);
  };
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <div style={styles.outer}>
      {isPending && <PendingOverlay name={user.name} onLogout={handleLogout} />}

      {/* Global write freeze — sits above everything, including the pending overlay case */}
      <MaintenanceBanner />

      {/* A88 — admin notice or an outage that affects this seller */}
      {!isPending && <StatusBanner />}

      {/* Full-width announcement strip — sits above the whole app, doesn't push the dashboard layout */}
      {!isPending && <RechargeReminder />}

      <div style={styles.shell}>

      {/* Graphite sidebar (v1.238.0). The slot reserves the page's share of the width (64px in
          auto-hide, full when pinned); the panel itself is sticky inside it and, when hover-opened,
          simply overflows the slot and floats over the page — so the page never re-flows on hover. */}
      {narrow && drawer && <div style={styles.drawerScrim} onClick={() => setDrawer(false)} />}
      <div style={{ ...styles.slot, width: narrow ? 0 : (pinned ? FULL_W : RAIL_W) }}>
      <aside
        style={narrow
          ? { ...styles.sidebar, ...styles.drawer, width: FULL_W, transform: drawer ? 'translateX(0)' : `translateX(-${FULL_W + 20}px)` }
          : { ...styles.sidebar, width: expanded ? FULL_W : RAIL_W, boxShadow: expanded && !pinned ? '14px 0 40px rgba(0,0,0,0.22)' : 'none' }}
        onMouseEnter={pinned || narrow ? undefined : onRailEnter}
        onMouseLeave={pinned || narrow ? undefined : onRailLeave}
      >

        {/* Brand */}
        <div style={styles.brand}>
          {/* Closed: just the white mark — the left slice of the white wordmark (the favicon is a
              white tile, which reads as a hole in a dark rail). */}
          <span style={{ ...styles.brandMark, opacity: collapsed ? 1 : 0 }}><img src="/logo-white.png" alt="" style={{ height: 30, display: 'block' }} /></span>
          <img src="/logo-white.png" alt="ModCommerce" style={{ ...styles.brandFull, opacity: collapsed ? 0 : 1 }} />
          <button
            style={{ ...styles.pinBtn, ...(pinned ? styles.pinBtnOn : null), opacity: collapsed ? 0 : 1, pointerEvents: collapsed ? 'none' : 'auto', ...(narrow ? { display: 'none' } : null) }}
            onClick={togglePin}
            title={pinned ? 'Unpin — let the menu hide itself  ( [ )' : 'Pin — keep the menu open  ( [ )'}
            aria-pressed={pinned}
          >
            {pinned ? <PinOff size={15} /> : <Pin size={15} />}
          </button>
        </div>

        {/* Navigation */}
        <nav style={styles.nav}>
          {visibleNav(NAV, user?.access).map(n => renderNav(n))}
        </nav>

        {/* Set-once pages, kept out of the daily list */}
        <div style={styles.bottomNav}>
          {visibleNav(BOTTOM_NAV, user?.access).map(n => renderNav(n))}
        </div>

        {/* A88 — System Status dot + quick-look popup */}
        {!isPending && <StatusWidget collapsed={collapsed} dark />}

        {/* Wallet */}
        <WalletWidget collapsed={collapsed} dark onBilling={canOpen(HIDDEN_NAV[0], user?.access) ? () => navigate('/dashboard/billing') : undefined} />

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.avatar}>{initials}</div>
          <div style={{ ...styles.userInfo, opacity: collapsed ? 0 : 1 }}>
            <div style={styles.userName}>{user?.name}</div>
            {user?.client_id && <div style={styles.clientId}>{user.client_id}</div>}
          </div>
          <div style={{ ...styles.menuWrap, opacity: collapsed ? 0 : 1, pointerEvents: collapsed ? 'none' : 'auto' }} ref={menuRef}>
            <button style={styles.dotMenuBtn} onClick={() => setMenuOpen(o => !o)} title="Options">
              <MoreVertical size={15} />
            </button>
            {menuOpen && (
              <div style={styles.dropdown}>
                <button
                  style={styles.dropdownItem}
                  onClick={() => { setMenuOpen(false); navigate('/dashboard/profile'); }}
                >
                  <Settings size={13} style={{ flexShrink: 0 }} />
                  Profile Settings
                </button>
                <div style={styles.dropdownDivider} />
                <button
                  style={{ ...styles.dropdownItem, color: '#ef4444' }}
                  onClick={handleLogout}
                >
                  <LogOut size={13} style={{ flexShrink: 0 }} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      </div>

      {/* Light Main Content */}
      <main className="mc-main" style={narrow ? { ...styles.main, ...styles.mainNarrow } : styles.main}>
        {narrow && (
          <div style={styles.mobileBar}>
            <button style={styles.mobileMenuBtn} onClick={() => setDrawer(true)} aria-label="Open menu"><Menu size={20} /></button>
            <img src="/logo-color.png" alt="ModCommerce" style={{ height: 22 }} />
          </div>
        )}
        {children}
      </main>
      </div>
      {/* Floating guided-setup pill + wizard (fixed position, doesn't affect layout) */}
      {!isPending && <SetupChecklist />}
      {!isPending && <WelcomePopup />}
      {/* Only approved, logged-in users see the "What's New" popup (never pending accounts). */}
      {user?.approval_status === 'approved' && <ReleaseNotesModal />}
      {/* Bulk action progress: floating cards + the expanded modal. Rendered here, not by the
          page, so navigating away never interrupts a running batch. */}
      {!isPending && <BulkProgressCards />}
      <NavSearch open={searchOpen} onClose={() => setSearchOpen(false)} pages={searchPages} onGo={(path) => navigate(path)} />
    </div>
  );
}

// v1.238.0 - graphite sidebar
const RAIL_W = 64, FULL_W = 244;
const EASE = 'cubic-bezier(.32,.72,0,1)';
const styles = {
  outer: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#F5F5F7' },
  shell: {
    display: 'flex', flex: 1, alignItems: 'flex-start', background: '#F5F5F7',
  },
  // Reserves the page's share of the width; stretches to the page height so the sticky panel inside
  // it stays put while the page scrolls.
  slot: { flexShrink: 0, alignSelf: 'stretch', position: 'relative', zIndex: 60, transition: `width 260ms ${EASE}` },
  sidebar: {
    background: '#111114',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column',
    transition: `width 260ms ${EASE}, box-shadow 260ms ${EASE}`,
    position: 'sticky', top: 0, height: '100vh',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
    WebkitFontSmoothing: 'antialiased',
  },
  brand: {
    position: 'relative', padding: '0 12px 0 15px', height: 64,
    display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
  },
  brandMark: { position: 'absolute', left: 20, width: 24, height: 30, overflow: 'hidden', transition: 'opacity 180ms ease' },
  brandFull: { height: 30, objectFit: 'contain', transition: 'opacity 180ms ease' },
  pinBtn: {
    marginLeft: 'auto', width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent',
    color: '#8E8E93', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'opacity 180ms ease, background 150ms ease, color 150ms ease',
  },
  pinBtnOn: { background: 'rgba(255,255,255,0.09)', color: '#FFFFFF' },
  navLabel: { transition: 'opacity 150ms ease', overflow: 'hidden', textOverflow: 'ellipsis' },
  // v1.239.0 - groups / search
  groupRow: { width: '100%', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5 },
  subLink: { height: 32, paddingLeft: 41, fontSize: 13 },
  guide: { position: 'absolute', left: 20, top: 4, bottom: 4, width: 1, background: 'rgba(255,255,255,0.08)' },
  chev: { flexShrink: 0, transition: `transform 200ms ${EASE}, opacity 150ms ease` },
  searchRow: { width: '100%', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, color: '#A1A1A6', background: 'rgba(255,255,255,0.04)', margin: '2px 0 4px' },
  kbd: { fontSize: 10.5, color: '#8E8E93', background: 'rgba(255,255,255,0.07)', borderRadius: 5, padding: '2px 6px', transition: 'opacity 150ms ease', whiteSpace: 'nowrap' },
  bottomNav: { padding: '6px 10px 2px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 },
  navSection: { position: 'relative', color: '#6E6E73', fontSize: 11, fontWeight: 600, padding: '14px 11px 6px', height: 33, whiteSpace: 'nowrap', boxSizing: 'border-box' },
  navDivider: { position: 'absolute', left: 14, right: 14, top: 16, height: 1, background: 'rgba(255,255,255,0.08)' },
  navPro: { marginLeft: 'auto', background: 'rgba(167,139,250,0.2)', color: '#C4B5FD', fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, borderRadius: 5, padding: '2px 6px' },
  navProOn: { marginLeft: 'auto', background: 'rgba(255,255,255,0.22)', color: '#ffffff', fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, borderRadius: 5, padding: '2px 6px' },
  navBadge: { marginLeft: 'auto', background: '#FF453A', color: '#fff', fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '1px 7px', minWidth: 18, textAlign: 'center', lineHeight: '16px' },
  badgeDot: { position: 'absolute', top: 7, left: 29, width: 7, height: 7, borderRadius: '50%', background: '#FF453A', border: '1.5px solid #111114' },
  nav: { flex: 1, padding: '4px 10px 8px', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' },
  navLink: { display: 'flex', alignItems: 'center', gap: 12, height: 36, padding: '0 11px', borderRadius: 9, position: 'relative', flexShrink: 0, textDecoration: 'none', transition: 'background 150ms ease, color 150ms ease', whiteSpace: 'nowrap', overflow: 'hidden', fontSize: 13.5 },
  footer: { padding: '10px 12px 12px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 11, minHeight: 58, flexShrink: 0, whiteSpace: 'nowrap' },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#5E5CE6,#BF5AF2)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, letterSpacing: 0.5, marginLeft: 3 },
  userInfo: { flex: 1, minWidth: 0, overflow: 'hidden', transition: 'opacity 150ms ease' },
  userName: { color: '#F5F5F7', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  clientId: { color: '#8E8E93', fontSize: 11, marginTop: 2, fontWeight: 500 },
  menuWrap: { position: 'relative', flexShrink: 0, marginLeft: 'auto', transition: 'opacity 150ms ease' },
  dotMenuBtn: { background: 'none', border: 'none', color: '#8E8E93', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  dropdown: {
    position: 'absolute', bottom: 36, right: 0,
    background: '#ffffff', border: '1px solid #e2e8f0',
    borderRadius: 10, minWidth: 185,
    boxShadow: 'var(--mc-e3)', zIndex: 300, overflow: 'hidden',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', background: 'none', border: 'none',
    color: '#374151', padding: '10px 14px',
    textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 500,
  },
  dropdownDivider: { height: 1, background: '#f1f5f9' },
  main: { flex: 1, padding: '24px 28px', minWidth: 0, background: '#F5F5F7', minHeight: '100vh' },
  // Narrow screens: less padding, and wide content scrolls INSIDE the page instead of pushing the
  // whole viewport sideways (most pages were laid out for desktop widths).
  mainNarrow: { padding: '10px 12px 24px', width: '100%', overflowX: 'auto' },
  drawer: { position: 'fixed', left: 0, top: 0, zIndex: 1200, boxShadow: '14px 0 40px rgba(0,0,0,0.3)', transition: `transform 240ms ${EASE}` },
  drawerScrim: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1150 },
  mobileBar: { display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 12px', position: 'sticky', left: 0 },
  mobileMenuBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', cursor: 'pointer' },
};
